import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Calendar } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { triggerUFOAnimation } from './SubmissionUFOAnimation';

const FounderConsultationModal = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.name || !formData.email || !formData.phone) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please fill in all required fields.",
      });
      return;
    }

    const submitBtn = e.currentTarget.querySelector('button[type="submit"]') || e.target;
    triggerUFOAnimation(submitBtn || e);

    // Simulate API call/storage
    const consultations = JSON.parse(localStorage.getItem('founder_consultations') || '[]');
    consultations.push({ ...formData, timestamp: new Date().toISOString() });
    localStorage.setItem('founder_consultations', JSON.stringify(consultations));

    toast({
      title: "Request Sent!",
      description: "We'll get back to you shortly to schedule your consultation.",
    });

    setFormData({ name: '', email: '', phone: '', message: '' });
    onClose();
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/60"
        />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative glass-panel-white w-full max-w-lg overflow-hidden z-10"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#181F32] to-[#181F32] p-6 text-white flex justify-between items-start border-b border-white/20">
            <div>
              <h3 className="text-xl font-extrabold font-heading flex items-center gap-2">
                <Calendar className="text-[#F3453F]" size={20} />
                Book Founder Consultation
              </h3>
              <p className="text-white/80 text-xs mt-1 font-sans">
                Schedule a 1-on-1 strategic growth session with Sahil Sharma
              </p>
            </div>
            <button 
              onClick={onClose}
              className="btn-glass btn-glass-dark btn-glass-sm p-1.5 focus:outline-none"
            >
              <X size={18} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4 font-sans">
            <div>
              <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="glass-input"
                placeholder="Sahil Sharma"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="glass-input"
                  placeholder="sahil@example.com"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="glass-input"
                  placeholder="+91 93109 71677"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">Project Details / Goals</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={3}
                className="glass-input resize-none"
                placeholder="Briefly describe your business goals..."
              />
            </div>

            <button
              type="submit"
              className="w-full btn-glass btn-glass-submit py-3.5 mt-2"
            >
              Confirm Booking
              <Send size={18} className="ml-2 inline-block" />
            </button>
            
            <p className="text-xs text-center text-gray-400 mt-2">
              Confidentiality assured. We respond within 4 business hours.
            </p>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default FounderConsultationModal;